/**
 * SB6-II-01 — Bulk-assign-users dialog (AC23).
 *
 * Opened from the toolbar "Assign users" action when ≥1 row is selected.
 * Multi-select `<Autocomplete>` over `assignable-users`; Save fires a
 * single `POST /bulk-assign` carrying the selected instruction ids + the
 * chosen user ids, then invalidates the list (each CREATED row flips to
 * SCHEDULED on the BE).
 */
import { useState, type ReactElement } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useToast } from "../account/ToastContext.js";
import { useIiAssignableUsersQuery } from "./useInspectionInstructionFacets.js";
import { useBulkAssignMutation } from "./mutations.js";
import type { AssignedUser } from "./types.js";
import { userLabel } from "./userLabel.js";

export interface BulkAssignDialogProps {
  open: boolean;
  onClose: () => void;
  instructionIds: readonly string[];
}

export function BulkAssignDialog({
  open,
  onClose,
  instructionIds,
}: BulkAssignDialogProps): ReactElement {
  const { toast } = useToast();
  const assignable = useIiAssignableUsersQuery(open);
  const bulk = useBulkAssignMutation();
  const [selected, setSelected] = useState<AssignedUser[]>([]);

  function closeAndReset(): void {
    setSelected([]);
    onClose();
  }

  function onSave(): void {
    bulk.mutate(
      {
        inspectionInstructionIds: [...instructionIds],
        userIds: selected.map((u) => u.userId),
      },
      {
        onSuccess: () => {
          toast(
            `Assigned ${String(selected.length)} user${
              selected.length === 1 ? "" : "s"
            } to ${String(instructionIds.length)} instruction${
              instructionIds.length === 1 ? "" : "s"
            }.`,
          );
          closeAndReset();
        },
        onError: () => {
          toast("Couldn't assign users. Try again.", "error");
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={closeAndReset}
      maxWidth="sm"
      fullWidth
      aria-labelledby="bulk-assign-heading"
    >
      <DialogTitle id="bulk-assign-heading">Assign users</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assign one or more users to {String(instructionIds.length)} selected
          inspection instruction{instructionIds.length === 1 ? "" : "s"}.
        </Typography>
        <Autocomplete<AssignedUser, true>
          multiple
          disableCloseOnSelect
          openOnFocus
          size="small"
          options={assignable.data?.users ?? []}
          value={selected}
          loading={assignable.isPending}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(a, b) => a.userId === b.userId}
          onChange={(_e, next) => {
            setSelected(next);
          }}
          renderTags={(picked, getTagProps) =>
            picked.map((o, index) => {
              const { key, ...rest } = getTagProps({ index });
              return (
                <Chip {...rest} key={key} size="small" label={userLabel(o)} />
              );
            })
          }
          renderInput={(p) => {
            const { InputLabelProps, InputProps, inputProps, ...rest } = p;
            const tfProps = {
              ...rest,
              label: "Users",
              placeholder: "Search users…",
              slotProps: {
                htmlInput: { ...inputProps, "aria-label": "Users" },
                input: InputProps,
                inputLabel: { ...InputLabelProps, shrink: true },
              },
            } as unknown as React.ComponentProps<typeof TextField>;
            return <TextField {...tfProps} />;
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeAndReset} disabled={bulk.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={selected.length === 0 || bulk.isPending}
          startIcon={
            bulk.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
